import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { KNOWN_ROUTE_SEGMENTS } from '../../app-routing.module';
import { GroupLinkService } from '../services/group-link.service';

/**
 * Guard to validate group links before route activation.
 *
 * Checks if the first URL segment is a group link (not in KNOWN_ROUTE_SEGMENTS).
 * If it is, validates it with the backend before allowing navigation.
 */
export const groupLinkGuard: CanActivateFn = route => {
  const router = inject(Router);
  const groupLinkService = inject(GroupLinkService);

  const groupLinkParam = route.paramMap.get('groupLink');

  // If no group link parameter, allow navigation
  if (!groupLinkParam) {
    return true;
  }

  // Check if the parameter matches a known route segment
  // If it does, it's not a group link, just a regular route
  if (KNOWN_ROUTE_SEGMENTS.includes(groupLinkParam)) {
    return true;
  }

  // At this point, we have a potential group link - validate with backend
  return groupLinkService.validateGroupLink(groupLinkParam).pipe(
    map(isValid => {
      if (isValid) {
        // Store the valid group link
        groupLinkService.setGroupLink(groupLinkParam);
        return true;
      } else {
        // Invalid group link - redirect to not found
        router.navigate(['/not-found'], {
          queryParams: {
            reason: 'invalid-group-link',
            groupLink: groupLinkParam,
          },
        });
        return false;
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Handle different error scenarios
      let reason = 'unknown-error';
      let errorCode: string | undefined;

      if (error.status === 403 || error.status === 404) {
        reason = 'invalid-group-link';
        // Try to extract error code from response
        if (error.error?.error?.code) {
          errorCode = error.error.error.code;
        }
      } else if (error.status === 0) {
        // Network error
        reason = 'network-error';
      }

      router.navigate(['/not-found'], {
        queryParams: {
          reason,
          groupLink: groupLinkParam,
          ...(errorCode && { errorCode }),
        },
      });

      return of(false);
    })
  );
};
